import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { PermissionsContext } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  MerchantRoutes,
  MerchantRoutesContext,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { memo, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { OrderManagementSuiteStatusesContext } from "./order-management-cloud";

export const MerchantRouteNavigate = memo(function MerchantRoute({
  routeTo,
}: {
  routeTo: (routes: MerchantRoutes) => string;
}) {
  const navigate = useNavigate();
  const routes = useContext(MerchantRoutesContext);

  useEffect(() => {
    if (!routes) {
      return;
    }
    const href = routeTo(routes);
    navigate(href, { replace: true });
  }, [navigate, routes, routeTo]);

  return null;
});

export const MerchantRoutesProvider = memo(function MerchantRoutesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useContext(AuthContext);
  const team = useContext(TeamContext);

  const { omsEnabled, warrantiesEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const permissions = useContext(PermissionsContext);

  const teamId = auth?.teamId;

  const baseUrl = teamId ? `/stores/${teamId}` : "";
  const storeBaseUrl = baseUrl;
  const homeBaseHref = `${storeBaseUrl}/home`;
  const orderManagementBaseUrl = `${baseUrl}/order-management`;
  const inventoryManagementBaseUrl = `${baseUrl}/inventory`;
  const marketingBaseUrl = `${baseUrl}/marketing`;
  const supportBaseUrl = `${baseUrl}/support`;
  const conversionBaseUrl = `${baseUrl}/conversion`;
  const customersBaseUrl = `${baseUrl}/customer`;
  const financeBaseUrl = `${baseUrl}/finance`;
  const aeoBaseUrl = `${baseUrl}/aeo`;

  const claimsHref = `${orderManagementBaseUrl}/claims`;

  const orderManagementDefaultRoute = useMemo(() => {
    const canManageShipments = permissions?.has(
      OrderManagementPermissions.MANAGE_SHIPMENTS,
    );
    const canScanAndVerify = permissions?.has(
      OrderManagementPermissions.SCAN_AND_VERIFY,
    );
    const canViewReturns = permissions?.has(
      OrderManagementPermissions.VIEW_RETURN,
    );
    const canManageWarranties = permissions?.has(
      OrderManagementPermissions.MANAGE_WARRANTY,
    );
    const canViewOrders = permissions?.has(
      OrderManagementPermissions.VIEW_ORDER,
    );
    const canViewDashboard = permissions?.has(
      OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
    );

    const dashboardRoute = `${orderManagementBaseUrl}/outbound-labels/dashboard`;
    const omsDefault = `${orderManagementBaseUrl}/outbound-labels/awaiting-shipment`;
    const ordersDefault = `${orderManagementBaseUrl}/outbound-labels/orders`;

    // If newOmsEnabled is true, default to dashboard
    if (team?.settings?.outboundLabels?.newOmsEnabled && canViewDashboard) {
      return dashboardRoute;
    }

    if (omsEnabled && canManageShipments) {
      return omsDefault;
    } else if (canViewOrders) {
      return ordersDefault;
    } else if (canViewReturns) {
      return `${orderManagementBaseUrl}/returns`;
    } else if (omsEnabled && canScanAndVerify) {
      return `${orderManagementBaseUrl}/outbound-labels/scan`;
    } else if (canManageWarranties && warrantiesEnabled) {
      return `${orderManagementBaseUrl}/warranties`;
    }
    return omsEnabled ? omsDefault : ordersDefault;
  }, [
    permissions,
    omsEnabled,
    warrantiesEnabled,
    orderManagementBaseUrl,
    team,
  ]);

  const claimsDefaultRoute = useMemo(() => {
    const canManagePackageProtectionClaims = permissions?.has(
      OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    );

    const canManageCarrierClaims = permissions?.has(
      OrderManagementPermissions.MANAGE_CARRIER_CLAIM,
    );

    const packageProtectionEnabled =
      !!team?.settings.packageProtection?.enabled;

    const omsEnabled = !!team?.settings.outboundLabels?.newOmsEnabled;
    const isReturnEnabled = team?.settings.returns?.enabled;

    if (canManagePackageProtectionClaims && packageProtectionEnabled) {
      return `${claimsHref}/all`;
    } else if (canManageCarrierClaims && (omsEnabled || isReturnEnabled)) {
      return `${claimsHref}/carrier-claims`;
    } else {
      return `${claimsHref}/all`;
    }
  }, [team?.settings, claimsHref, permissions]);

  const merchantRoutes: MerchantRoutes = useMemo(
    () => ({
      // Home routes
      overviewHref: `${homeBaseHref}/overview`,
      spotlightsHref: `${homeBaseHref}/spotlights`,
      getSpotlightsSubRoute: (subPath: string) =>
        `${homeBaseHref}/spotlights/${subPath}`,
      analyticsHref: `${homeBaseHref}/analytics`,
      getAnalyticsSubRoute: (subPath: string) =>
        `${homeBaseHref}/analytics/${subPath}`,
      homeBaseHref,

      // Order management routes
      orderManagementDefaultRoute,
      pickSessionHref: `${orderManagementBaseUrl}/outbound-labels/pick-sessions`,
      pickSessionTableHref: `${orderManagementBaseUrl}/outbound-labels/admin-pick-sessions`,
      transferHref: `${orderManagementBaseUrl}/warehouse/transfer`,
      instantTransferHref: `${orderManagementBaseUrl}/warehouse/instant-transfer`,
      consolidationHref: `${orderManagementBaseUrl}/warehouse/consolidation`,

      ordersOutboundLabelsHref: `${orderManagementBaseUrl}/outbound-labels`,
      getOrdersOutboundLabelsSubRoute: (subPath: string) =>
        `${orderManagementBaseUrl}/outbound-labels/${subPath}`,

      returnsHref: `${orderManagementBaseUrl}/returns`,
      getReturnsSubRoute: (subPath: string) =>
        `${orderManagementBaseUrl}/returns/${subPath}`,

      claimsHref,
      claimsDefaultRoute,
      getClaimsSubRoute: (subPath: string) => `${claimsHref}/${subPath}`,

      getWarrantiesHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${orderManagementBaseUrl}/warranties/all-warranties`
          : `${orderManagementBaseUrl}/warranties/about`,
      warrantiesHref: `${orderManagementBaseUrl}/warranties`,
      getWarrantiesSubRoute: (subPath: string) =>
        `${orderManagementBaseUrl}/warranties/${subPath}`,
      registrationsHref: `${orderManagementBaseUrl}/registrations`,

      warehouseHref: `${orderManagementBaseUrl}/warehouse`,
      warehouseBaseUrl: `${orderManagementBaseUrl}/warehouse`,
      returnsProcessingHref: `${orderManagementBaseUrl}/warehouse/processing`,

      getRecycleHref: () => `${orderManagementBaseUrl}/recycle/all-recycle`,
      recycleHref: `${orderManagementBaseUrl}/recycle`,
      getRecycleSubRoute: (subPath: string) =>
        `${orderManagementBaseUrl}/recycle/${subPath}`,

      tariffCalculatorHref: `${orderManagementBaseUrl}/outbound-labels/tariff-calculator`,
      orderEditsHref: `${orderManagementBaseUrl}/order-edits`,
      classifyProductsHref: `${orderManagementBaseUrl}/classify-products`,
      analyticsOrderFulfillmentHref: `${orderManagementBaseUrl}/analytics/order-fulfillment`,
      getAnalyticsOrderFulfillmentSubRoute: (subPath: string) =>
        `${orderManagementBaseUrl}/analytics/order-fulfillment?tab=${subPath}`,
      analyticsReturnsClaimsHref: `${orderManagementBaseUrl}/analytics/returns-claims`,
      getAnalyticsReturnsClaimsSubRoute: (subPath: string) =>
        `${orderManagementBaseUrl}/analytics/returns-claims/${subPath}`,
      analyticsReportsHref: `${orderManagementBaseUrl}/analytics/reports`,
      analyticsReturnsProcessingHref: `${orderManagementBaseUrl}/analytics/returns-processing`,

      // Inventory routes
      getInventoryHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${inventoryManagementBaseUrl}/products/all`
          : `${inventoryManagementBaseUrl}/onboarding`,

      inventoryProductsHref: `${inventoryManagementBaseUrl}/catalog/products`,

      // Marketing routes
      marketingAnalyticsHref: `${marketingBaseUrl}/analytics`,
      automationsHref: `${marketingBaseUrl}/automations`,

      getTrackingHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${marketingBaseUrl}/tracking`
          : `${marketingBaseUrl}/tracking/about`,

      getTrackingSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/tracking/${subPath}`,

      recoverHref: `${marketingBaseUrl}/recover`,
      getRecoverSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/recover/${subPath}`,

      reviewsHref: `${marketingBaseUrl}/reviews/dashboard`,
      getReviewsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/reviews/${subPath}`,

      emailSmsHref: `${marketingBaseUrl}/email-sms`,
      getEmailSmsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/email-sms/${subPath}`,

      loyaltyHref: `${marketingBaseUrl}/loyalty/program`,
      getLoyaltySubRoute: (subPath: string) =>
        `${marketingBaseUrl}/loyalty/${subPath}`,

      marketingAgentHref: `${marketingBaseUrl}/marketing-agent`,
      getMarketingAgentSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/marketing-agent/${subPath}`,

      // Support routes
      getSupportHref: ({ enabled }: { enabled: boolean }) =>
        enabled ? `${supportBaseUrl}/table/all` : `${supportBaseUrl}/about`,

      getSupportTableHref: (viewName: string) =>
        `${supportBaseUrl}/table/${encodeURIComponent(viewName)}`,
      getSupportTableSubRoute: (subPath: string) =>
        `${supportBaseUrl}/table/${subPath}`,
      getConciergeHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${supportBaseUrl}/dashboard`
          : `${supportBaseUrl}/concierge/preview`,
      getConciergeSubRoute: (subPath: string) =>
        `${supportBaseUrl}/concierge/${subPath}`,

      getSupportDevSubRoute: (subPath: string) =>
        `${supportBaseUrl}/dev/${subPath}`,

      // Conversion routes
      conversionAnalyticsHref: `${conversionBaseUrl}/analytics`,
      getConversionHref: ({
        enabled,
        checkoutOutboundRatesEnabled,
      }: {
        enabled: boolean;
        checkoutOutboundRatesEnabled: boolean;
      }) => {
        if (enabled && checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/dashboard`;
        } else if (enabled && !checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/onboard`;
        } else {
          return `${conversionBaseUrl}/welcome`;
        }
      },

      // Marketing routes
      getMarketingHref: ({
        emailSmsEnabled,
        marketingCloudEnabled,
      }: {
        emailSmsEnabled: boolean;
        marketingCloudEnabled: boolean;
      }) => {
        if (emailSmsEnabled) {
          return `${marketingBaseUrl}/analytics`;
        } else if (marketingCloudEnabled) {
          return `${marketingBaseUrl}/automations`;
        } else {
          return `${marketingBaseUrl}/welcome`;
        }
      },

      getSegmentBuilderRoute({
        teamId,
        segmentId,
      }: {
        teamId: string;
        segmentId: string;
      }) {
        return `${storeBaseUrl}/marketing/segments/${segmentId}`;
      },

      getCustomerRecordHref: ({ customerId }: { customerId?: string }) =>
        customerId
          ? `${customersBaseUrl}/${customerId}`
          : `${customersBaseUrl}/all-customers`,

      getConversionSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/${subPath}`,

      // Settings routes
      settingsHref: `${storeBaseUrl}/settings`,
      getSettingsSubRoute: (subPath: string) =>
        `${storeBaseUrl}/settings/${subPath}`,

      // Customer portal builder route
      customerPortalBuilderHref: `${storeBaseUrl}/customer-portal-builder`,

      // Base URL helpers
      orderManagementBaseUrl,
      inventoryManagementBaseUrl,
      marketingBaseUrl,
      supportBaseUrl,
      conversionBaseUrl,
      storeBaseUrl,
      customersBaseUrl,
      aeoBaseUrl,

      // Finance routes
      financeBaseUrl,
      disputesHref: `${financeBaseUrl}/disputes`,
      disputesDashboardHref: `${financeBaseUrl}/disputes/dashboard`,
      disputesChargebacksHref: `${financeBaseUrl}/disputes/chargebacks`,
      disputesSettingsHref: `${financeBaseUrl}/disputes/settings`,

      // AEO routes
      aeoDashboardHref: `${aeoBaseUrl}/dashboard`,
      aeoConfigureHref: `${aeoBaseUrl}/configure`,
      aeoContentHref: `${aeoBaseUrl}/content`,
      aeoPromptsHref: `${aeoBaseUrl}/prompts`,
      aeoSourcesHref: `${aeoBaseUrl}/sources`,
      aeoCatalogHref: `${aeoBaseUrl}/catalog`,
      getAeoSubRoute: (subPath: string) => `${aeoBaseUrl}/${subPath}`,
      getAeoCatalogSubRoute: (subPath: string) =>
        `${aeoBaseUrl}/catalog/${subPath}`,
    }),
    [
      homeBaseHref,
      orderManagementBaseUrl,
      inventoryManagementBaseUrl,
      marketingBaseUrl,
      aeoBaseUrl,
      financeBaseUrl,
      supportBaseUrl,
      conversionBaseUrl,
      customersBaseUrl,
      storeBaseUrl,
      orderManagementDefaultRoute,
      claimsHref,
      claimsDefaultRoute,
    ],
  );

  return (
    <MerchantRoutesContext.Provider value={merchantRoutes}>
      {children}
    </MerchantRoutesContext.Provider>
  );
});

// Convenience hook
export function useMerchantRoutes(): MerchantRoutes {
  return useRequiredContext(MerchantRoutesContext);
}
